import React, { useState, useEffect, useRef } from 'react';
import PhoneNumberInput from '../common/PhoneNumberInput';
import customerService from '../../services/customer.service';
import { cleanPhoneNumber } from '../../helpers/phone';
import './CustomerPicker.css';

/**
 * CustomerPicker - Static form with auto-detection
 *
 * Looks exactly like the old static form (Name, Apellido, Teléfono).
 * Auto-detects existing customers by phone number in the background.
 * Shows inline prompt when match found.
 *
 * @param {Object} value - { firstName, lastName, phoneNumber, customerId }
 * @param {Function} onChange - Called with updated value object
 * @param {Object} validation - { firstNameValid, lastNameValid } for external validation state
 */
export default function CustomerPicker({ value = {}, onChange, validation = {} }) {
    const { firstName = '', lastName = '', phoneNumber = '', customerId = null } = value;
    const { firstNameValid = true, lastNameValid = true } = validation;

    const [matchedCustomer, setMatchedCustomer] = useState(null);
    const [showPrompt, setShowPrompt] = useState(false);
    const [isLinked, setIsLinked] = useState(!!customerId);
    const debounceRef = useRef(null);

    // Auto-search when phone has 10+ digits
    useEffect(() => {
        const digits = cleanPhoneNumber(phoneNumber);

        // Clear any pending search
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        if (digits.length >= 10 && !isLinked) {
            // Debounce the search
            debounceRef.current = setTimeout(() => {
                searchCustomer(phoneNumber);
            }, 500);
        } else {
            setMatchedCustomer(null);
            setShowPrompt(false);
        }

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [phoneNumber, isLinked]);

    // Sync isLinked state with customerId prop
    useEffect(() => {
        setIsLinked(!!customerId);
    }, [customerId]);

    const searchCustomer = async (phone) => {
        try {
            const existing = await customerService.findByPhone(phone);
            if (existing) {
                setMatchedCustomer(existing);
                setShowPrompt(true);
            } else {
                setMatchedCustomer(null);
                setShowPrompt(false);
            }
        } catch (error) {
            // Silently fail - not critical
            console.error('Customer search error:', error);
        }
    };

    const handleUseExisting = () => {
        onChange({
            firstName: matchedCustomer.firstName,
            lastName: matchedCustomer.lastName,
            phoneNumber: matchedCustomer.phoneNumber,
            customerId: matchedCustomer.id
        });
        setIsLinked(true);
        setShowPrompt(false);
    };

    const handleIgnore = () => {
        setShowPrompt(false);
        setMatchedCustomer(null);
    };

    const handleUnlink = () => {
        setIsLinked(false);
        onChange({ ...value, customerId: null });
    };

    const handleFieldChange = (field, fieldValue) => {
        onChange({ ...value, [field]: fieldValue });
    };

    return (
        <div className="customer-picker">
            <div className="formEntry">
                <label className="form-label">Nombre</label>
                <input
                    type="text"
                    className={firstNameValid ? 'validInputField' : 'invalidInputField'}
                    value={firstName}
                    onChange={(e) => handleFieldChange('firstName', e.target.value)}
                    disabled={isLinked}
                />
            </div>

            <div className="formEntry">
                <label className="form-label">Apellido</label>
                <input
                    type="text"
                    className={lastNameValid ? 'validInputField' : 'invalidInputField'}
                    value={lastName}
                    onChange={(e) => handleFieldChange('lastName', e.target.value)}
                    disabled={isLinked}
                />
            </div>

            <PhoneNumberInput
                value={phoneNumber}
                onChange={(val, isValid) => {
                    handleFieldChange('phoneNumber', val);
                }}
                label="Teléfono"
                placeholder="+19107422001"
                disabled={isLinked}
            />

            {/* Inline prompt when match found */}
            {showPrompt && matchedCustomer && (
                <div className="customer-picker-prompt">
                    <span className="customer-picker-prompt-text">
                        {matchedCustomer.firstName} {matchedCustomer.lastName} ya existe
                    </span>
                    <div className="customer-picker-prompt-buttons">
                        <button type="button" className="customer-picker-btn-use" onClick={handleUseExisting}>
                            Usar datos
                        </button>
                        <button type="button" className="customer-picker-btn-ignore" onClick={handleIgnore}>
                            Ignorar
                        </button>
                    </div>
                </div>
            )}

            {/* Small badge when linked to existing customer */}
            {isLinked && (
                <div className="customer-picker-linked">
                    <span>✓ Cliente existente</span>
                    <button type="button" onClick={handleUnlink}>Cambiar</button>
                </div>
            )}
        </div>
    );
}
